/**
 * The 3D articulation model's poses (issue #25), built to the reviewed spec
 * docs/design/articulation-spec.md. Coordinates in millimetres in the midsagittal plane: origin
 * at the upper central incisors' cutting edge, `y` up, `z` forward (back of the mouth is negative).
 * The mouth's own anatomy (teeth, palate, jaw) comes from BodyParts3D (public/articulator/mouth.glb,
 * CC BY 4.0); the numbers below were sampled from it (ml/src/foe_ml/articulator).
 *
 * Each pose is an example of a typical configuration, not an exact template (the spec's review);
 * articulation.test.ts checks every pose against the spec's rules.
 */

/** A point on the tongue's upper surface along the midline: [z, y] in mm. */
export type ProfilePoint = readonly [number, number];

export type PoseId = 'rest' | 'rBunched' | 'rRetroflex' | 'w' | 'l';

export interface Pose {
  id: PoseId;
  /** Upper surface of the tongue along the midline, from the tip back and down to the root. */
  profile: readonly ProfilePoint[];
  /**
   * Height of the tongue's sides relative to the midline at each profile point, in mm: positive
   * raises the sides above the midline (lateral bracing with a central groove), negative lowers
   * them (the lateral channels of /l/).
   */
  sides: readonly number[];
  /** Width scale of the tongue body (compressed sides for /l/). */
  width: number;
  /** Lips: opening height and width (mm) and protrusion forward of the incisors (mm). */
  lips: { height: number; width: number; protrusion: number };
  /** Lower jaw opening, degrees about the jaw joint (1 degree opens the front teeth about 1.7 mm). */
  jaw: number;
  /** Soft palate raised (oral sounds) or lowered (breathing at rest). */
  velumRaised: boolean;
}

/** The hard palate's oral surface along the midline: [z, y] (sampled from the maxilla mesh). */
export const PALATE: readonly ProfilePoint[] = [
  [-4, 11.5],
  [-8, 12.1],
  [-12, 14],
  [-16, 16.5],
  [-20, 20],
  [-25, 22],
  [-30, 22.4],
  [-35, 23.1],
];

/** The soft palate (velum) when raised: from the hard palate's end, back and down. */
export const VELUM_RAISED: readonly ProfilePoint[] = [
  [-37, 23],
  [-44, 22],
  [-51, 19],
  [-57, 15],
];
export const VELUM_LOWERED: readonly ProfilePoint[] = [
  [-37, 23],
  [-43, 18],
  [-48, 12],
  [-51, 6],
];

/** Back wall of the throat (pharynx), at this depth. */
export const PHARYNX_WALL_Z = -72;

/** Tongue profile point order: tip, blade, front, mid, back x2, root x3. */
export const PROFILE_PARTS = [
  'tip',
  'blade',
  'front',
  'mid',
  'back',
  'back',
  'root',
  'root',
  'root',
] as const;

export const POSES: Readonly<Record<PoseId, Pose>> = {
  // Relaxed: tip behind the lower front teeth, nothing raised or retracted, velum down.
  rest: {
    id: 'rest',
    profile: [
      [-6, -6],
      [-14, -2],
      [-22, 2],
      [-32, 4],
      [-42, 4],
      [-52, 1],
      [-59, -8],
      [-61, -18],
      [-58, -28],
    ],
    sides: [0, 0, 1, 1, 1, 0, 0, 0, 0],
    width: 1,
    lips: { height: 4, width: 36, protrusion: 0 },
    jaw: 2,
    velumRaised: false,
  },
  // Bunched /r/: blade and front raised toward the palate without touching; the back dips low;
  // root drawn back toward the throat; sides braced (central groove); lips slightly rounded,
  // open; the tip low, may rest behind the lower teeth.
  rBunched: {
    id: 'rBunched',
    profile: [
      [-8, -4],
      [-14, 6],
      [-21, 14],
      [-29, 15],
      [-40, 6],
      [-50, 2],
      [-60, -6],
      [-65, -17],
      [-62, -28],
    ],
    sides: [0, 2, 4, 4, 3, 2, 0, 0, 0],
    width: 1,
    lips: { height: 6, width: 24, protrusion: 4 },
    jaw: 1.8,
    velumRaised: true,
  },
  // Retroflex /r/: the tip raised and turned slightly back, behind the alveolar ridge without
  // contact (a modest curl, not an exaggerated one); the back dips low; root drawn back; sides
  // braced; lips slightly rounded.
  rRetroflex: {
    id: 'rRetroflex',
    profile: [
      [-16, 9],
      [-12, 5],
      [-19, 3],
      [-28, 6],
      [-40, 4],
      [-50, 1],
      [-60, -6],
      [-65, -17],
      [-62, -28],
    ],
    sides: [0, 1, 3, 4, 3, 2, 0, 0, 0],
    width: 1,
    lips: { height: 10, width: 28, protrusion: 2 },
    jaw: 2.5,
    velumRaised: true,
  },
  // /w/ (example of the gliding error): one constriction, the back of the tongue raised toward
  // the velum; tip low; no bracing or root retraction; lips small, tight, round and pushed out.
  w: {
    id: 'w',
    profile: [
      [-7, -7],
      [-14, -4],
      [-22, 0],
      [-32, 6],
      [-42, 13],
      [-50, 12],
      [-58, 1],
      [-60, -14],
      [-58, -28],
    ],
    sides: [0, 0, 1, 1, 1, 0, 0, 0, 0],
    width: 1,
    lips: { height: 5, width: 12, protrusion: 8 },
    jaw: 2.5,
    velumRaised: true,
  },
  // /l/ (example of the lateral error): the tip touches the alveolar ridge; the sides compressed
  // inward, leaving channels for the air; a fairly dark /l/ (mild root retraction).
  l: {
    id: 'l',
    profile: [
      [-8, 11.5],
      [-14, 9],
      [-22, 7],
      [-32, 5],
      [-42, 4],
      [-52, 1],
      [-60, -8],
      [-63, -19],
      [-60, -29],
    ],
    sides: [0, -2, -5, -6, -5, -3, 0, 0, 0],
    width: 0.82,
    lips: { height: 6, width: 34, protrusion: 0 },
    jaw: 2.5,
    velumRaised: true,
  },
};

/**
 * Width of the tongue (mm) along the profile, at width scale 1. It fits inside the dental arch: the
 * sides rest against the molars' inner surfaces (about 38 mm apart), not through them.
 */
export const TONGUE_WIDTH = [12, 24, 32, 36, 38, 36, 32, 28, 22] as const;

/** The palate's height (mm) above the incisal-edge level at depth `z`, by linear interpolation. */
export function palateY(z: number): number | undefined {
  for (let i = 0; i + 1 < PALATE.length; i++) {
    const a = PALATE[i];
    const b = PALATE[i + 1];
    if (!a || !b) continue;
    if (z <= a[0] && z >= b[0]) return a[1] + ((b[1] - a[1]) * (a[0] - z)) / (a[0] - b[0]);
  }
  return undefined;
}

/**
 * The floor of the mouth along the midline, back to front: [z, y] mm. Registered from the coach
 * diagrams (Tavin's mid-sagittal drawings; ml/experiments/sagittal_overlay.py), so the 2D view's
 * tongue meets the drawn floor; the 3D view uses the same outline.
 */
export const FLOOR: readonly ProfilePoint[] = [
  [-52, -23],
  [-44, -20],
  [-35, -20],
  [-27, -21.5],
  [-20, -23.5],
  [-13, -25],
  [-8, -24],
];

/**
 * The tongue's underside just behind the tip: mm behind and below the tip, in the tip's own frame
 * (along the way it points, then across).
 */
const TIP_UNDERSIDE: ProfilePoint = [-1.5, -4];
/** Points on the free underside, from the floor's front up to the tip. */
const UNDERSIDE_POINTS = 6;

/** Samples along the tongue's upper surface in its outline. */
export const OUTLINE_SAMPLES = 44;

/** A pose in between two poses (`t` 0: `a`, 1: `b`). */
export function blendPoses(a: Pose, b: Pose, t: number) {
  const mix = (x: number, y: number) => x + (y - x) * t;
  return {
    profile: a.profile.map((p, i) => {
      const q = b.profile[i] ?? p;
      return [mix(p[0], q[0]), mix(p[1], q[1])] as const;
    }),
    sides: a.sides.map((s, i) => mix(s, b.sides[i] ?? s)),
    width: mix(a.width, b.width),
    lips: {
      height: mix(a.lips.height, b.lips.height),
      width: mix(a.lips.width, b.lips.width),
      protrusion: mix(a.lips.protrusion, b.lips.protrusion),
    },
    jaw: mix(a.jaw, b.jaw),
    velum: mix(a.velumRaised ? 1 : 0, b.velumRaised ? 1 : 0),
  };
}

export type PoseState = ReturnType<typeof blendPoses>;

/** One move into the pose and back, in seconds. */
export const CYCLE_S = 3.4;

/**
 * How far into the pose the model is at `seconds`: rest, move in, hold, move back (one cycle),
 * eased so it starts and stops softly.
 */
export function poseWeight(seconds: number): number {
  const phase = (seconds % CYCLE_S) / CYCLE_S;
  const k =
    phase < 0.15
      ? 0
      : phase < 0.35
        ? (phase - 0.15) / 0.2
        : phase < 0.8
          ? 1
          : 1 - (phase - 0.8) / 0.2;
  return k * k * (3 - 2 * k);
}

/** `count` points evenly spaced along a smooth curve (Catmull-Rom) through `points`. */
export function smoothCurve(points: readonly ProfilePoint[], count: number): ProfilePoint[] {
  const dense: ProfilePoint[] = [];
  const at = (i: number) => points[Math.max(0, Math.min(points.length - 1, i))] ?? [0, 0];
  for (let i = 0; i + 1 < points.length; i++) {
    const [p0, p1, p2, p3] = [at(i - 1), at(i), at(i + 1), at(i + 2)];
    for (let k = 0; k < 16; k++) {
      const t = k / 16;
      const c = (a: number, b: number, c2: number, d: number) =>
        0.5 *
        (2 * b +
          (-a + c2) * t +
          (2 * a - 5 * b + 4 * c2 - d) * t * t +
          (-a + 3 * b - 3 * c2 + d) * t * t * t);
      dense.push([c(p0[0], p1[0], p2[0], p3[0]), c(p0[1], p1[1], p2[1], p3[1])]);
    }
  }
  dense.push(at(points.length - 1));
  // Resample by arc length.
  const lengths = [0];
  for (let i = 1; i < dense.length; i++) {
    const a = dense[i - 1] ?? [0, 0];
    const b = dense[i] ?? [0, 0];
    lengths.push((lengths[i - 1] ?? 0) + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  const total = lengths[lengths.length - 1] ?? 0;
  const out: ProfilePoint[] = [];
  let j = 1;
  for (let k = 0; k < count; k++) {
    const target = (k / (count - 1)) * total;
    while (j < lengths.length - 1 && (lengths[j] ?? 0) < target) j++;
    const a = dense[j - 1] ?? [0, 0];
    const b = dense[j] ?? [0, 0];
    const la = lengths[j - 1] ?? 0;
    const f = Math.min(1, Math.max(0, (target - la) / Math.max(1e-9, (lengths[j] ?? 0) - la)));
    out.push([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f]);
  }
  return out;
}

/** Points in the tongue's outline: the upper surface, the floor, the free underside. */
export const OUTLINE_POINTS = OUTLINE_SAMPLES + FLOOR.length + UNDERSIDE_POINTS + 1;

/** A point of the tongue's midline outline. */
export interface OutlinePoint {
  z: number;
  y: number;
  /** Position along the tongue (0 tip, 1 root end), for its width and side height. */
  along: number;
  /** On the upper surface (its sides rise or fall with the pose). */
  top: boolean;
}

/**
 * The tongue's midsagittal outline, as in MRI tracings: the upper surface from the tip back and
 * down to the root, then the floor of the mouth forward, then the free underside, a smooth curve
 * from the floor up to just below the tip (with the sublingual hollow under a raised tip).
 * Closed and simple (articulation.test.ts checks it for every pose and in between).
 */
export function tongueOutline(profile: readonly ProfilePoint[]): OutlinePoint[] {
  const surface = smoothCurve(profile, OUTLINE_SAMPLES);
  const out: OutlinePoint[] = surface.map(([z, y], i) => ({
    z,
    y,
    along: i / (OUTLINE_SAMPLES - 1),
    top: true,
  }));
  const [back, front] = [FLOOR[0]?.[0] ?? -52, FLOOR[FLOOR.length - 1]?.[0] ?? -8];
  for (const [z, y] of FLOOR) {
    out.push({ z, y, along: 0.15 + ((z - front) / (back - front)) * 0.7, top: false });
  }
  // The free underside: from the floor's front, bulging slightly forward, up to below the tip.
  const floorFront = FLOOR[FLOOR.length - 1] ?? [-8, -24];
  const [tz, ty] = surface[0] ?? [0, 0];
  // Below and behind the tip, relative to the way the tip points: for a tip curled back, its
  // underside faces forward.
  const [nz, ny] = surface[1] ?? [tz - 1, ty];
  const len = Math.hypot(tz - nz, ty - ny) || 1;
  const [dz, dy] = [(tz - nz) / len, (ty - ny) / len];
  const end: ProfilePoint = [
    tz + TIP_UNDERSIDE[0] * dz - TIP_UNDERSIDE[1] * dy,
    ty + TIP_UNDERSIDE[0] * dy + TIP_UNDERSIDE[1] * dz,
  ];
  // A raised tip leaves a hollow below it (the sublingual space, articulation-spec.md).
  const hollow = Math.min(10, Math.max(0, ty));
  // A tip curled back leaves the blade in front: the underside stays in front of the blade
  // (blended in as the curl grows, so the animation does not jump).
  const frontZ = Math.max(...surface.slice(0, 8).map(([z]) => z));
  const curl = Math.min(1, Math.max(0, (frontZ - tz - 0.5) / 2));
  const hollowZ = Math.max(floorFront[0], end[0]) + 1 - hollow;
  const curledZ = Math.max(floorFront[0], frontZ) + 2;
  const mid: ProfilePoint = [hollowZ + (curledZ - hollowZ) * curl, (floorFront[1] + end[1]) / 2];
  const underside = smoothCurve([floorFront, mid, end], UNDERSIDE_POINTS + 2).slice(1, -1);
  for (const [z, y] of [...underside, end]) {
    out.push({ z, y, along: 0.04, top: false });
  }
  return out;
}
